import { FilterSelect, Input } from '@wundo/ui';
import type { MeetingKind } from '@wundo/shared/calendar.ts';
import { m } from '@/paraglide/messages.js';

/** What the list is narrowed to. `all` is every kind, which is where it starts. */
export type ConversationKindFilter = MeetingKind | 'all';

export interface ConversationsToolbarProps {
  search: string;
  onSearchChange: (search: string) => void;
  kind: ConversationKindFilter;
  onKindChange: (kind: ConversationKindFilter) => void;
}

/**
 * Search, and what kind of conversation to look through.
 *
 * The kind is the one thing this list can be narrowed by: what a conversation
 * is about, which gesprekstype it follows, stays off an overview anyone can
 * read over a shoulder, so it is no filter either.
 *
 * Still no "new conversation": a conversation is here because it is in the
 * calendar and Wundo follows it, so there is nothing to create from this page.
 */
export function ConversationsToolbar({
  search,
  onSearchChange,
  kind,
  onKindChange,
}: ConversationsToolbarProps) {
  return (
    <div className='flex flex-wrap items-center gap-3'>
      <Input
        type='search'
        className='min-w-50 flex-1'
        aria-label={m.conversations_search_label()}
        placeholder={m.conversations_search_placeholder()}
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
      />
      {/*
        The three kinds a conversation can be. "Overig" is named here although
        such a row wears no pill: a card has nothing to say about a conversation
        that is neither, but somebody looking for their customer calls has to be
        able to ask for them by name.
      */}
      <FilterSelect
        label={m.conversations_kind_label()}
        options={
          [
            { value: 'all', label: m.conversations_kind_all() },
            { value: 'one_on_one', label: m.meeting_kind_one_on_one() },
            { value: 'team', label: m.meeting_kind_team() },
            { value: 'other', label: m.conversations_kind_other() },
          ] as const
        }
        value={kind}
        onValueChange={onKindChange}
      />
    </div>
  );
}
